/**
 * CUSTOMER ORDERS (item 4, ADR-012).
 *
 * The stage the reviewer found missing: what the customer asked for, recorded
 * and APPROVED before anything moves. Approval is the moment the price is
 * fixed (DEF-103) — a later change to the customer's card cannot reprice this
 * order — so the screen says what approval does before the button is pressed.
 *
 * 22 Sep — rebuilt after the owner found it confusing. The register shows ONE
 * row per agreement (an amended-away version is history, not a row), each row
 * wears ONE word for where it stands, the next action is at the top of the
 * page, and the counts are the three that decide an action: ordered,
 * dispatched, outstanding. The amendment lineage and the holds are drawn only
 * when there is something in them.
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go, routeQuery } from '../hooks.js';
import { Banner, Button, Card, Count, Loading, Pill, Register, Section, Seg, Select, Text, TextArea, type Column, Serial } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { CustomerPickerById } from '../pickers.js';
import { NextStep } from '../next.js';
import { orderStanding, challanStanding, actionWords } from '../standing.js';
// `qty` is imported as qtyText: this screen had its own copy of it, which
// grouped nothing and printed 6.600 Nm³ where the shared one prints 6.6.
import { count, date, money, qty as qtyText, qtyByUnit, today, when, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';
import { LineEditor, emptyLine, firstLine, toLineBodies, type LineRow } from '../lines.js';
import type { FulfilmentDetail } from '../../../src/views/fulfilment.js';

const MESSAGES = screenById('orders')!.messages;

export function Orders({ id, session }: { id: string | null; session: Session }) {
  if (id === 'new') return <NewOrder />;
  if (id) return <OneOrder orderId={Number(id)} session={session} />;
  return <OrderRegister session={session} />;
}

type Show = 'open' | 'draft' | 'all';
const SHOW_QUERY: Record<Show, { status?: 'draft' | 'approved'; outstanding?: true }> = {
  open: { status: 'approved', outstanding: true },
  draft: { status: 'draft' },
  all: {},
};
const SHOW_EMPTY: Record<Show, string> = {
  open: 'Nothing is open — every approved order has been sent. That is good news, not a gap.',
  draft: 'Nothing is waiting for approval.',
  all: 'No order has been taken yet.',
};

function OrderRegister({ session }: { session: Session }) {
  // the customer page sends the register here for one customer (#/orders?customer=12)
  const customerId = routeQuery('customer');
  const [show, setShow] = useState<Show>(customerId ? 'all' : 'open');
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('orders.list', { ...SHOW_QUERY[show], ...(customerId ? { customerId: Number(customerId) } : {}), current: true, page, pageSize: 50 }), [show, customerId, page], MESSAGES);
  const customerName = asked.data?.rows[0]?.customerName ?? null;

  // 0164 — the figures are what is OWED, in the unit the customer ordered in.
  // An order mixing Nm3 and kg has no single figure, so it shows the breakdown.
  const columns: readonly Column<{
    orderId: number; customerName: string; siteName: string; customerReference: string | null; requestedDate: string;
    status: string; unit: string | null; qtyPromised: number | null; qtyDispatched: number | null; qtyOutstanding: number | null;
    promisedByUnit: Record<string, number>; outstandingByUnit: Record<string, number>;
    cylsDispatched: number; cylsEstimated: number; fulfilment: string;
  }>[] = [
    { key: 'customer', head: 'Customer', cell: (r) => <><strong>{r.customerName}</strong><div className="muted">{r.siteName}</div></> },
    { key: 'ref', head: 'Their reference', cell: (r) => r.customerReference ?? <span className="muted">—</span> },
    { key: 'wanted', head: 'Wanted by', cell: (r) => date(r.requestedDate) },
    { key: 'ordered', head: 'Ordered', right: true, cell: (r) => r.unit ? qtyText(r.qtyPromised, r.unit) : qtyByUnit(r.promisedByUnit) },
    // one vocabulary with the order's own page: SUPPLIED (what actually went,
    // which a full cylinder can push past the order) and STILL TO SEND
    { key: 'dispatched', head: 'Supplied', right: true, cell: (r) =>
        <>{r.unit ? qtyText(r.qtyDispatched, r.unit) : <span className="muted">—</span>}
          {r.cylsDispatched ? <div className="muted">{count(r.cylsDispatched)} cylinders</div> : null}</> },
    { key: 'outstanding', head: 'Still to send', right: true, cell: (r) => <strong>{r.unit ? qtyText(r.qtyOutstanding, r.unit) : qtyByUnit(r.outstandingByUnit)}</strong> },
    { key: 'standing', head: 'Where it stands', cell: (r) => { const s = orderStanding(r.status, r.fulfilment); return <Pill tone={s.tone}>{s.word}</Pill>; } },
  ];

  return (
    <Page title="Customer orders" sub="What each customer asked for, and how much of it is still to send."
          actions={can(session, 'order.create') ? <Button kind="primary" onClick={() => go('#/orders/new')}>New order</Button> : null}>
      <div className="toolbar" style={{ alignItems: 'center' }}>
        <span><span className="seg-label">Show</span>
          <Seg name="show" value={show} onChange={(v) => { setShow(v as Show); setPage(1); }}
               options={[{ value: 'open', label: 'Still to send' }, { value: 'draft', label: 'Waiting for approval' }, { value: 'all', label: 'All' }]} /></span>
        {customerId ? <span className="muted">Only {customerName ?? 'one customer'}'s orders · <a href="#/orders" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/orders'); }}>show every customer</a></span> : null}
      </div>
      <Card>
        {asked.loading ? <Loading what="orders" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty={SHOW_EMPTY[show]}
                        onOpen={(r) => go(`#/orders/${r.orderId}`)}
 />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </Page>
  );
}

function OneOrder({ orderId, session }: { orderId: number; session: Session }) {
  const asked = useAsk(() => api.view('orders.get', { orderId }), [orderId], MESSAGES);
  const middle = useAsk(() => api.view('orders.fulfilment', { orderId }), [orderId], MESSAGES);
  const runner = useRun(MESSAGES);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');

  if (asked.loading && !asked.data) return <Page title="Order"><Loading what="the order" /></Page>;
  if (asked.error) return <Page title="Order"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const o = d.order;
  const f = middle.data ?? null;
  const supersededById = f?.order.supersededById ?? null;
  const current = supersededById === null;
  const standing = orderStanding(o.status, o.fulfilment, supersededById);
  const latestChallan = d.challans.length ? d.challans[d.challans.length - 1]! : null;
  const reload = () => { asked.reload(); middle.reload(); };

  async function approve() {
    const ok = await runner.run(() => api.command('order.approve', { orderId }));
    if (ok) reload();
  }
  async function cancel() {
    const ok = await runner.run(() => api.command('order.cancel', { orderId, reason: reason.trim() }));
    if (ok) { setCancelling(false); setReason(''); reload(); }
  }

  /** 0164 — anything still owed, in any unit the order uses. */
  const stillOwed = Object.values(o.outstandingByUnit).reduce((a, v) => a + v, 0);
  const owedWords = o.unit ? qtyText(o.qtyOutstanding, o.unit) : qtyByUnit(o.outstandingByUnit);
  const sentWords = o.unit ? qtyText(o.qtyDispatched, o.unit) : `${count(o.cylsDispatched)} cylinders`;
  // 0170 — HOW THE SUPPLIED FIGURE IS MADE UP, not how far it is "above the
  // order". An earlier draft led with the difference and the owner's answer
  // was "what are you talking about, each cylinder is 7 nm3": two shells at
  // seven is fourteen, which is the quantity and not an overage to excuse.
  const shellsOut = o.cylsDispatched;
  const mayPrepare = current && o.status === 'approved' && stillOwed > 0 && can(session, 'challan.create');

  return (
    <Page title={`Order for ${o.customerName}`}
          sub={`${o.siteName} · wanted by ${date(o.requestedDate)}${o.customerReference ? ` · their reference ${o.customerReference}` : ''}`}
          actions={<>
            {!current
              ? <Button kind="primary" onClick={() => go(`#/orders/${supersededById}`)}>Open the version in force</Button> : null}
            {current && o.status === 'draft' && can(session, 'order.approve')
              ? <Button kind="primary" onClick={() => { void approve(); }} disabled={runner.busy}>{runner.busy ? 'Approving…' : 'Approve the order'}</Button> : null}
            {mayPrepare
              ? <Button kind="primary" onClick={() => go(`#/dispatch/new?order=${orderId}`)}>Prepare a dispatch</Button> : null}
            {current && (o.status === 'draft' || o.status === 'approved') && stillOwed > 0 && can(session, 'order.cancel')
              ? <Button kind="danger" onClick={() => setCancelling((v) => !v)}>{cancelling ? 'Keep the order' : 'Cancel what is left'}</Button> : null}
          </>}>

      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

      {!current ? (
        <NextStep kind="warn" title={`This order was amended${f?.versions.find((v) => v.orderId === supersededById)?.amendedOn ? ` on ${date(f.versions.find((v) => v.orderId === supersededById)!.amendedOn)}` : ''}. A newer version of this order is in force.`}
                  detail="This one is kept as history. Nothing can be done to it." />
      ) : o.status === 'draft' ? (
        <NextStep title="This order is not approved yet."
                  detail="Approving it fixes the customer's agreed rates on this order. A later change to the rate card cannot reprice what is approved here." />
      ) : o.status === 'cancelled' ? (
        <NextStep kind="warn" title="This order was cancelled." {...(o.cancelReason ? { detail: o.cancelReason } : {})} />
      ) : stillOwed > 0 ? (
        <NextStep kind="good" title={o.cylsDispatched > 0 ? `Approved, ${sentWords} sent, ${owedWords} still to send.` : 'Approved. The yard can prepare a load against it.'}
                  detail={o.cylsOnLoad > 0 ? `${count(o.cylsOnLoad)} cylinders are on a load being checked.` : 'Prepare a dispatch: scan the cylinders, and somebody else checks the load before it leaves.'} />
      ) : o.fulfilment === 'delivered' ? (
        <NextStep kind="good" title="Delivered in full." detail="The empties come back on a return challan; the holding is on the customer's account." />
      ) : (
        <NextStep kind="good" title="Everything has left the plant." detail="Confirm the delivery from the challan when the truck reaches the gate."
                  {...(latestChallan ? { action: { label: 'Open the challan', href: `#/dispatch/${latestChallan.challanId}` } } : {})} />
      )}

      {cancelling ? (
        <Card title="Cancel what is still outstanding" sub="what has already gone out on a challan cannot be cancelled">
          <TextArea name="cancel-reason" label="Why it is cancelled" value={reason} onChange={setReason} />
          <Button kind="danger" onClick={() => { void cancel(); }} disabled={runner.busy || !reason.trim()}>
            {runner.busy ? 'Cancelling…' : 'Cancel the outstanding quantity'}
          </Button>
        </Card>
      ) : null}

      <div className="kpis">
        <div className="kpi" style={{ ['--accent' as string]: 'var(--primary)' }}>
          <div className="kpi-label">Ordered</div>
          <div className="kpi-value" style={{ fontSize: 22 }}>{o.unit ? qtyText(o.qtyPromised, o.unit) : qtyByUnit(o.promisedByUnit)}</div>
          <div className="kpi-note">{o.cylsEstimated ? `about ${count(o.cylsEstimated)} cylinders` : 'what the customer asked for'}</div>
        </div>
        <div className="kpi" style={{ ['--accent' as string]: 'var(--good)' }}>
          <div className="kpi-label">Supplied</div>
          <div className="kpi-value" style={{ fontSize: 22 }}>{sentWords}</div>
          <div className="kpi-note">{shellsOut
            ? `${count(shellsOut)} cylinder${shellsOut === 1 ? '' : 's'}${o.cylsDelivered ? `, ${count(o.cylsDelivered)} delivered` : ' left the plant'}`
            : 'nothing has gone yet'}</div>
        </div>
        <div className="kpi" style={{ ['--accent' as string]: 'var(--money)' }}>
          <div className="kpi-label">Still to send</div>
          <div className="kpi-value" style={{ fontSize: 22 }}>{owedWords}</div>
          <div className="kpi-note">{o.cylsOnLoad ? `${count(o.cylsOnLoad)} cylinders on a load being checked` : 'the order finishes when this reaches nothing'}</div>
        </div>
        <div className="kpi" style={{ ['--accent' as string]: 'var(--muted)' }}>
          <div className="kpi-label">Where it stands</div><div className="kpi-value" style={{ fontSize: 18 }}><Pill tone={standing.tone}>{standing.word}</Pill></div>
          <div className="kpi-note">{o.approvedAt ? `approved ${date(o.approvedAt)}` : `taken ${date(o.createdAt)}`}</div>
        </div>
      </div>

      <div className="grid two">
        <Card title="What was ordered">
          <Register
            columns={[
              { key: 'gas', head: 'Gas', cell: (l: { gasService: string; waterCapacityL: string | null }) =>
                  <>{words(l.gasService)}{l.waterCapacityL ? <span className="muted"> · {l.waterCapacityL} L</span> : null}</> },
              { key: 'ordered', head: 'Ordered', right: true, cell: (l: { qtyPromised: number; qtyUnit: string; qtyRequested: number; cylsEstimated: number | null }) =>
                  <>{qtyText(l.qtyPromised, l.qtyUnit)}
                    {l.qtyPromised < l.qtyRequested ? <div className="muted">{qtyText(l.qtyRequested - l.qtyPromised, l.qtyUnit)} cancelled</div>
                      : l.cylsEstimated !== null && l.qtyUnit !== 'cylinder' ? <div className="muted">about {count(l.cylsEstimated)} cylinders</div> : null}</> },
              { key: 'dispatched', head: 'Supplied', right: true, cell: (l: { qtyDispatched: number; qtyUnit: string; cylsDispatched: number; cylsDelivered: number }) =>
                  <>{qtyText(l.qtyDispatched, l.qtyUnit)}
                    {l.cylsDispatched ? <div className="muted">{count(l.cylsDispatched)} cylinder{l.cylsDispatched === 1 ? '' : 's'}{l.cylsDelivered ? `, ${count(l.cylsDelivered)} delivered` : ''}</div> : null}</> },
              { key: 'outstanding', head: 'Still to send', right: true, cell: (l: { qtyOutstanding: number; qtyUnit: string; cylsOnLoad: number }) =>
                  <><strong>{qtyText(l.qtyOutstanding, l.qtyUnit)}</strong>{l.cylsOnLoad ? <div className="muted">{count(l.cylsOnLoad)} being checked</div> : null}</> },
            ]}
            rows={d.lines} empty="This order has no lines." />
        </Card>

        <Card title="Who, and where">
          <div className="detail-grid">
            <div><div className="k">Customer</div><div className="v"><a href={`#/customers/${o.customerId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/customers/${o.customerId}`); }}>{o.customerName}</a></div></div>
            <div><div className="k">Delivery site</div><div className="v">{o.siteName}</div></div>
            <div><div className="k">Wanted by</div><div className="v">{date(o.requestedDate)}</div></div>
            <div><div className="k">Their reference</div><div className="v">{o.customerReference ?? '—'}</div></div>
            <div><div className="k">Account</div><div className="v"><a href={`#/account/${o.customerId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/account/${o.customerId}`); }}>holdings and money</a></div></div>
            {o.notes ? <div><div className="k">Notes</div><div className="v">{o.notes}</div></div> : null}
          </div>
        </Card>
      </div>

      <Card title="Challans against this order">
        <Register
          columns={[
            { key: 'number', head: 'Challan', cell: (c: { number: string | null; challanId: number }) => c.number ? <Serial>{c.number}</Serial> : <span className="muted">not issued yet</span> },
            { key: 'date', head: 'Date', cell: (c: { eventDate: string }) => date(c.eventDate) },
            { key: 'count', head: 'Cylinders', right: true, cell: (c: { lineCount: number }) => count(c.lineCount) },
            { key: 'standing', head: 'Where it stands', cell: (c: { status: string; journey: string | null }) => { const s = challanStanding('dispatch', c.status, c.journey); return <Pill tone={s.tone}>{s.word}</Pill>; } },
          ]}
          rows={d.challans} empty="Nothing has been sent against this order yet."
          onOpen={(c) => go(`#/dispatch/${c.challanId}`)} />
      </Card>

      {f ? <TheCommercialLedger f={f} /> : null}
      {f ? <OrderMiddle orderId={orderId} status={o.status} session={session} f={f} onChanged={reload} onHoldsChanged={middle.reload} /> : null}

      {d.audit.length ? (
        <Card title="What happened" sub="who did what, when, and why">
          <ul className="timeline">
            {d.audit.map((a) => (
              <li key={a.auditId}>
                <strong>{actionWords(a.action)}</strong> by {a.actorName ?? `user ${a.actorId}`}
                {a.reason ? <> — {a.reason}</> : null}
                <div className="when">{when(a.at)}</div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </Page>
  );
}

type Middle = FulfilmentDetail;

/**
 * WHAT WAS AGREED, WHAT WENT, WHAT WAS CHARGED (DEF-148).
 *
 * The owner's order page: *"Ordered 100 Nm³ · Delivered 70 · Billed 70 ·
 * Balance 30"*, and beneath it the challans and the bills that make those
 * figures up, so a clerk has the audit trail without opening six modules.
 *
 * Every figure here is in the unit the customer ORDERED in. The vessel counts
 * are still above, because the yard's question — how many cylinders went — is a
 * different question and both get asked. Neither is derived from the other.
 */
function TheCommercialLedger({ f }: { f: FulfilmentDetail }) {
  const lines = f.lines.filter((l) => l.orderedQty > 0);
  if (lines.length === 0) return null;
  const byLine = (n: number) => f.lines.find((l) => l.orderLineId === n);

  return (
    <div className="grid two">
      <Card title="Ordered, delivered, billed" sub="in the unit the customer ordered in — a cylinder is the container, not the sale">
        <Register
          columns={[
            { key: 'gas', head: 'Gas', cell: (l: FulfilmentDetail['lines'][number]) =>
                <>{words(l.gasService)}{l.waterCapacityL ? <span className="muted"> · {l.waterCapacityL} L</span> : null}</> },
            { key: 'ordered', head: 'Ordered', right: true, cell: (l: FulfilmentDetail['lines'][number]) =>
                <>{qtyText(l.orderedQty, l.qtyUnit)}{l.cancelledQty ? <div className="muted">{qtyText(l.cancelledQty, l.qtyUnit)} cancelled</div> : null}</> },
            // DEF-154 — the stages under their own names. "Gone" is what left the
            // gate and is what the memo charges for; "taken" is the customer's own
            // confirmation, which arrives later and is a different fact.
            { key: 'gone', head: 'Gone out', right: true, cell: (l: FulfilmentDetail['lines'][number]) =>
                <>{qtyText(l.dispatchedQty, l.qtyUnit)}
                  {l.committedQty > l.dispatchedQty
                    ? <div className="muted">{qtyText(l.committedQty - l.dispatchedQty, l.qtyUnit)} on a load, not yet gone</div> : null}
                  {l.deliveredQty < l.dispatchedQty
                    ? <div className="muted">{qtyText(l.deliveredQty, l.qtyUnit)} confirmed taken</div> : null}</> },
            { key: 'billed', head: 'Billed', right: true, cell: (l: FulfilmentDetail['lines'][number]) =>
                <>{qtyText(l.billedQty, l.qtyUnit)}{l.dispatchedQty > l.billedQty
                    ? <div className="muted">{qtyText(l.dispatchedQty - l.billedQty, l.qtyUnit)} not yet charged</div> : null}</> },
            { key: 'balance', head: 'Balance', right: true, cell: (l: FulfilmentDetail['lines'][number]) =>
                <strong>{qtyText(l.outstandingQty, l.qtyUnit)}</strong> },
          ]}
          rows={lines} empty="This order has no lines." />
        <div className="kpi-note" style={{ marginTop: 8 }}>Where it stands: {f.lifecycle}</div>
      </Card>

      <Card title="Deliveries and bills" sub="every challan that carried some of this order, and every bill raised for it">
        {f.deliveries.length === 0 && f.billings.length === 0
          ? <div className="muted">Nothing has gone out against this order yet.</div>
          : <ul className="trail">
              {f.deliveries.map((dv) => (
                <li key={`d${dv.challanId}-${dv.orderLineId}`}>
                  <strong>{dv.number ?? `challan ${dv.challanId}`}</strong> — {qtyText(dv.qty, dv.unit)}
                  <span className="muted">
                    {/* the vessel count only when it says something the quantity does not */}
                    {dv.unit === 'cylinder' ? '' : ` in ${dv.cylinders} cylinder${dv.cylinders === 1 ? '' : 's'}`}
                    {byLine(dv.orderLineId) ? ` · ${words(byLine(dv.orderLineId)!.gasService)}` : ''}</span>
                  <div className="when">{date(dv.eventDate)}</div>
                </li>
              ))}
              {f.billings.map((b) => (
                <li key={`b${b.billId}-${b.orderLineId}`}>
                  <strong>{b.number ?? `bill ${b.billId}`}</strong> — billed {qtyText(b.qty, b.unit)}
                  <span className="muted"> · {money(b.amountPaise)}</span>
                  <div className="when">{date(b.issuedOn)}</div>
                </li>
              ))}
            </ul>}
      </Card>
    </div>
  );
}

/**
 * THE ORDER'S MIDDLE (0049, M3-14) — the versions of the agreement and the
 * cylinders set aside for it. Both cards are drawn only when they hold
 * something or when the person may add to them; fulfilment is counted ONCE
 * across every version; a hold is released only in writing.
 */
function OrderMiddle({ orderId, status, session, f, onChanged, onHoldsChanged }: { orderId: number; status: string; session: Session; f: Middle; onChanged: () => void; onHoldsChanged: () => void }) {
  const runner = useRun(MESSAGES);
  const [amending, setAmending] = useState(false);
  const [amendReason, setAmendReason] = useState('');
  const [qtys, setQtys] = useState<Record<number, string>>({});
  const [holdLine, setHoldLine] = useState('');
  const [holdSerial, setHoldSerial] = useState('');
  const [releasing, setReleasing] = useState<Record<number, string>>({});

  const current = f.order.supersededById === null;
  const mayAmend = current && status === 'approved' && can(session, 'order.amend');
  const owed = Object.values(f.order.outstandingByUnit).reduce((a, v) => a + v, 0);
  const mayHold = current && status === 'approved' && owed > 0 && can(session, 'order.allocate');
  const showChanges = f.versions.length > 1 || mayAmend;
  const showHolds = f.holds.length > 0 || mayHold;

  async function amend() {
    const lines = f.lines
      // 0164 — an amendment restates the PROMISE, in the line's own unit.
      // It used to restate the cylinder estimate, which on a gas order was a
      // different quantity from the one the customer had agreed to.
      .filter((l) => l.cancelledQty < l.orderedQty)
      .map((l) => ({ orderLineId: l.orderLineId, qty: Number(qtys[l.orderLineId] ?? String(l.orderedQty)) }));
    const ok = await runner.run(async (retryKey) => {
      const r = await api.command('order.amend', { orderId, reason: amendReason.trim(), lines }, retryKey ? { idempotencyKey: retryKey } : {});
      const v = r.value as { orderId: number };
      if (v.orderId) go(`#/orders/${v.orderId}`);
      return r;
    });
    if (ok) { setAmending(false); setAmendReason(''); onChanged(); }
  }

  async function allocate() {
    const serial = holdSerial.trim();
    if (!serial || !holdLine) return;
    const ok = await runner.run(async (retryKey) => {
      // Allocating HOLDS a named cylinder against an order line, so the wrong
      // one here quietly promises a customer something nobody meant. Identity
      // is resolved by equality over the whole register, and an ambiguous
      // serial is refused rather than guessed.
      const r = await api.view('cylinders.resolve', { token: serial });
      if (r.total === 0) throw new Error(`No cylinder answers to ${serial}.`);
      if (r.total > 1) {
        throw new Error(r.collision
          ? `“${serial}” is one cylinder's own number and a different cylinder's sticker. Allocate it by its internal ID instead.`
          : `“${serial}” is on ${r.total} different cylinders. Allocate it by its internal ID instead.`);
      }
      const hit = r.candidates[0]!;
      return api.command('order.allocate', { orderLineId: Number(holdLine), cylinderId: hit.cylinderId }, retryKey ? { idempotencyKey: retryKey } : {});
    });
    if (ok) { setHoldSerial(''); onHoldsChanged(); }
  }

  async function release(allocationId: number) {
    const reason = (releasing[allocationId] ?? '').trim();
    if (!reason) return;
    const ok = await runner.run(() => api.command('order.release', { allocationId, reason }));
    if (ok) onHoldsChanged();
  }

  if (!showChanges && !showHolds) return null;

  return (
    <>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}

      {showChanges ? (
        <Card title="Changes to this order" sub="an order is changed by recording a new version, never by editing — what was sent is counted once across all of them"
              actions={mayAmend ? <Button small onClick={() => setAmending((v) => !v)}>{amending ? 'Leave it as it is' : 'Change the quantities'}</Button> : null}>
          {f.versions.length > 1 ? (
            <Register
              columns={[
                { key: 'v', head: 'Version', cell: (v: { versionNo: number; orderId: number }) => v.orderId === orderId ? <strong>{`v${v.versionNo} — this one`}</strong> : `v${v.versionNo}` },
                { key: 'standing', head: 'Where it stands', cell: (v: { status: string; orderId: number }) => { const s = orderStanding(v.status, 'open', v.orderId === f.order.orderId ? f.order.supersededById : (v.status === 'superseded' ? 1 : null)); return <Pill tone={s.tone}>{s.word}</Pill>; } },
                { key: 'why', head: 'Why it changed', cell: (v: { amendReason: string | null }) => v.amendReason ?? '—' },
                { key: 'when', head: 'When', cell: (v: { amendedOn: string | null }) => v.amendedOn ? date(v.amendedOn) : '—' },
              ]}
              rows={f.versions} empty="One version — nothing has been changed."
              onOpen={(v) => { if (v.orderId !== orderId) go(`#/orders/${v.orderId}`); }} />
          ) : !amending ? <p className="muted" style={{ margin: 0 }}>Nothing has been changed since it was taken.</p> : null}

          {amending ? (
            <div style={{ marginTop: 14 }}>
              {f.lines.filter((l) => l.cancelledQty < l.orderedQty).map((l) => (
                <div className="queue-item" key={l.orderLineId}>
                  <span className="grow">{words(l.gasService)}{l.waterCapacityL ? ` · ${l.waterCapacityL} L` : ''}
                    <span className="muted"> · {qtyText(l.committedQty, l.qtyUnit)} already on challans</span></span>
                  <Text name={`amend-qty-${l.orderLineId}`} label="" type="number"
                        value={qtys[l.orderLineId] ?? String(l.orderedQty)}
                        onChange={(v) => setQtys((old) => ({ ...old, [l.orderLineId]: v }))} />
                </div>
              ))}
              <TextArea name="amend-reason" label="Why the order is changing" value={amendReason} onChange={setAmendReason} />
              <Button kind="primary" onClick={() => { void amend(); }} disabled={runner.busy || !amendReason.trim()}>
                {runner.busy ? 'Recording…' : 'Record the change as a new version'}
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {showHolds ? (
        <Card title="Cylinders set aside for this order" sub="a cylinder set aside is promised to one line of this order; it is put back only in writing">
          <Register
            columns={[
              { key: 'cyl', head: 'Cylinder', cell: (h: { internalId: string }) => <Serial>{h.internalId}</Serial> },
              { key: 'serial', head: 'Serial', cell: (h: { serialNo: string | null }) => h.serialNo ?? '—' },
              { key: 'line', head: 'For', cell: (h: { orderLineId: number }) => {
                  const line = f.lines.find((l) => l.orderLineId === h.orderLineId);
                  return line ? `${words(line.gasService)}${line.waterCapacityL ? ` · ${line.waterCapacityL} L` : ''}` : `line ${h.orderLineId}`;
                } },
              { key: 'since', head: 'Set aside on', cell: (h: { allocatedOn: string }) => date(h.allocatedOn) },
              { key: 'release', head: '', cell: (h: { allocationId: number }) => can(session, 'order.release')
                  ? <span style={{ display: 'flex', gap: 6 }}>
                      <Text name={`release-${h.allocationId}`} label="" value={releasing[h.allocationId] ?? ''} placeholder="why it goes back"
                            onChange={(v) => setReleasing((old) => ({ ...old, [h.allocationId]: v }))} />
                      <Button small kind="danger" onClick={() => { void release(h.allocationId); }}
                              disabled={runner.busy || !(releasing[h.allocationId] ?? '').trim()}>Put it back</Button>
                    </span>
                  : null },
            ]}
            rows={f.holds} empty="Nothing is set aside for this order." />
          {mayHold ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 12 }}>
              <Select name="hold-line" label="Set aside for" value={holdLine} onChange={setHoldLine} placeholder="Choose a line"
                      options={f.lines.filter((l) => l.outstandingQty > 0).map((l) => ({ value: String(l.orderLineId), label: `${words(l.gasService)}${l.waterCapacityL ? ` · ${l.waterCapacityL} L` : ''} · ${qtyText(l.outstandingQty, l.qtyUnit)} outstanding` }))} />
              <Text name="hold-serial" label="Cylinder serial" value={holdSerial} onChange={setHoldSerial} placeholder="scan or type" />
              <Button onClick={() => { void allocate(); }} disabled={runner.busy || !holdLine || !holdSerial.trim()}>Set it aside</Button>
            </div>
          ) : null}
        </Card>
      ) : null}
    </>
  );
}

function NewOrder() {
  const [customerId, setCustomerId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [reference, setReference] = useState('');
  const [requested, setRequested] = useState(today());
  const [notes, setNotes] = useState('');
  // F04 — what he wants comes first, in the unit he thinks in; then who he is
  // The size is NOT prefilled (audit, 9 Oct 2026). "46.7" sat in the box,
  // was submitted with every order, and was then ENFORCED at dispatch against
  // each cylinder's water capacity — so a gas order, which needs no size at
  // all, quietly refused a 40 L shell. Gas first, quantity, unit; the
  // container is optional and stays blank until somebody means it.
  const [lines, setLines] = useState<LineRow[]>([{ ...emptyLine(), unit: 'cylinder' }]);
  const runner = useRun(MESSAGES);

  const skus = useAsk(() => api.view('skus.list', { active: true, pageSize: 200 }), [], MESSAGES);
  const gases = useAsk(() => api.view('gases.list', {}), [], MESSAGES);
  const sites = useAsk(() => (customerId ? api.view('sites.list', { customerId: Number(customerId), active: true }) : Promise.resolve({ rows: [] })), [customerId], MESSAGES);
  // 11 Oct — the first row starts on the first product (CTS1), not on "a gas by name"
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || !skus.data) return;
    setSeeded(true);
    // only an untouched row is seeded — somebody may already be typing
    setLines((cur) => (cur.length === 1 && !cur[0]!.skuId && !cur[0]!.gasService && !cur[0]!.rupees ? [firstLine(skus.data!.rows, false)] : cur));
  }, [skus.data, seeded]);

  async function submit() {
    const ok = await runner.run(async () => {
      const r = await api.command('order.create', {
        customerId: Number(customerId), siteId: Number(siteId), requestedDate: requested,
        ...(reference.trim() ? { customerReference: reference.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        lines: toLineBodies(lines),
      });
      const value = r.value as { orderId?: number };
      if (value?.orderId) go(`#/orders/${value.orderId}`);
      return r;
    });
    if (!ok) return;
  }

  const ready = !!customerId && !!siteId && toLineBodies(lines).length > 0;

  return (
    <Page title="New order" sub="What the customer asked for, in the unit they think in. Nothing moves until it is approved."
          actions={<Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !ready}>{runner.busy ? 'Recording…' : 'Record the order'}</Button>}>
      <Card>
        {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
        <Section n={1} title="Items" desc="what they asked for, in the unit they think in">
          <LineEditor rows={lines} skus={skus.data?.rows ?? []} gases={gases.data?.rows ?? []} onChange={setLines} idPrefix="" />
        </Section>

        <Section n={2} title="Buyer and delivery">
          <div className="grid two">
            <div>
              <CustomerPickerById name="customer" label="Customer" customerId={customerId}
                                  onPick={(v) => { setCustomerId(v); setSiteId(''); }} placeholder="Choose a customer" />
              <Select name="site" label="Deliver to (site)" value={siteId} onChange={setSiteId} placeholder={customerId ? 'Choose a site' : 'Choose the customer first'}
                      options={(sites.data?.rows ?? []).map((s) => ({ value: String(s.siteId), label: s.name + (s.isPrimary ? ' (primary)' : '') }))} />
            </div>
            <div>
              <Text name="reference" label="Their order / PO number" value={reference} onChange={setReference} help="what the customer calls this order" />
              <Text name="requested" label="Wanted by" type="date" value={requested} onChange={setRequested} required />
            </div>
          </div>
        </Section>

        <Section n={3} title="Notes">
          <TextArea name="notes" label="Anything the yard or the driver should know" value={notes} onChange={setNotes} />
          <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !ready}>
            {runner.busy ? 'Recording…' : 'Record the order'}
          </Button>
          <p className="muted" style={{ marginBottom: 0 }}>After it is recorded, a manager approves it; then the yard prepares the load.</p>
        </Section>
      </Card>
    </Page>
  );
}
